import { OtcProductStatus, RxFillRequestStatus } from '@truepill/tpos-types'
import type { PackingAdditonalData, Task } from 'providers/TaskProvider'
import type {
  Otc,
  PackingMaterial,
  PackingMaterialItem,
  RXFillRequest,
  OTCProduct,
  OverrideItem,
  SerialNumbersByOtcProduct,
} from 'types'

export const generateTasks = ({
  rxFillRequests,
  otcProducts,
  otcs,
  packingMaterialItems,
  packingMaterials,
  isLabelPrinted,
  existingTasks,
  orderInTriage,
}: {
  rxFillRequests: RXFillRequest[]
  otcProducts?: OTCProduct[]
  otcs?: Otc[]
  packingMaterialItems?: PackingMaterialItem[]
  packingMaterials?: PackingMaterial[]
  isLabelPrinted?: boolean
  existingTasks?: Task[]
  orderInTriage: boolean
}): Task[] => {
  const tasks: Task[] = []
  if (orderInTriage) return tasks // No tasks when order is in triage

  if (otcProducts && otcProducts.length > 0) {
    otcProducts.forEach(otcProduct => {
      const otc = otcs?.find(otc => otc.sku === otcProduct.sku && otc.upc.length > 0)
      const otcProductIsCancelled = otcProduct.otcStatus === OtcProductStatus.Cancelled
      if (otc && !otcProductIsCancelled) {
        const scansNeeded = otcProduct.quantity / otc.quantity
        const scansNumberNotValid = !Number.isInteger(scansNeeded)

        if (scansNumberNotValid) return

        for (let index = 0; index < scansNeeded; index++) {
          const existingTask = existingTasks?.find(({ key }) => key === `${otcProduct._id}-${index}`)
          const overrideScan = existingTask?.overrideScan || otcProduct.overrideScan || false
          const completed = (isLabelPrinted || existingTask?.completed || overrideScan) ?? false
          const persistedSerialNumber = otc.isSerialized ? otcProduct.serialNumbers?.[index] : undefined
          tasks.push({
            label: `${otcProduct.sku}`,
            key: `${otcProduct._id}-${index}`,
            overrideScan,
            code: otcProduct.sku,
            completed,
            productType: 'OTC',
            additionalData: existingTask?.additionalData || {
              upcOptions: otc.upc,
              isSerialized: otc.isSerialized || false,
              ...(persistedSerialNumber ? { manualSerialNumber: persistedSerialNumber } : {}),
            },
          })
        }
      }
    })
  }

  if (rxFillRequests && rxFillRequests.some(({ fill }) => fill.status === 'Complete')) {
    rxFillRequests
      .filter(fillRequest => fillRequest.status !== RxFillRequestStatus.Cancelled)
      .forEach(({ fill }) => {
        const existingTask = existingTasks?.find(({ key }) => key === fill.shortFillCode)
        const completed = (isLabelPrinted || existingTask?.completed) ?? false
        // Can't have an rxFillRequest on an order in Packing without an active Fill
        tasks.push({
          label: `Scan Rx label bar code for ${fill.rxFillCode}`,
          key: fill.shortFillCode,
          code: fill.shortFillCode,
          completed,
          productType: 'RX',
        })
      })
  }

  // Create tasks based on the quantiy for each packing material item that has a UPC
  if (packingMaterials && packingMaterials.length > 0 && packingMaterialItems && packingMaterialItems.length > 0) {
    packingMaterialItems.forEach(packingMaterialItem => {
      const packingMaterial = packingMaterials?.find(pm => pm.sku === packingMaterialItem.sku && pm.upc.length > 0)
      // If the packing material item has an overrideScan flag, skip it
      if (packingMaterial) {
        const scansNeeded = packingMaterialItem.quantity / packingMaterial.quantity
        const scansNumberNotValid = !Number.isInteger(scansNeeded)

        if (scansNumberNotValid) return

        for (let index = 0; index < scansNeeded; index++) {
          const existingTask = existingTasks?.find(({ key }) => key === `${packingMaterialItem._id}-${index}`)
          const overrideScan = existingTask?.overrideScan || packingMaterialItem.overrideScan || false
          const completed = (isLabelPrinted || existingTask?.completed || overrideScan) ?? false
          tasks.push({
            label: `${packingMaterial.itemName}`,
            key: `${packingMaterialItem._id}-${index}`,
            overrideScan,
            code: packingMaterialItem.sku,
            completed,
            productType: 'PM',
            additionalData: { upcOptions: packingMaterial.upc },
          })
        }
      }
    })
  }
  return tasks
}

export const getScannedUpcs = (tasks: Task[]): string => {
  const upcs = tasks.reduce((acc, task) => {
    const upc = task.additionalData?.upcScanned
    if (upc) {
      if (acc) {
        acc = acc + ', ' + upc
      } else {
        acc = upc
      }
    }
    return acc
  }, '')

  return upcs || ''
}

export const getScannedSerialNumbers = (tasks: Task[]): string[] => {
  const serialNumbers = tasks.reduce((acc: string[], task) => {
    const serialNumber = task.additionalData?.scannedSerialNumber
    if (serialNumber) {
      acc.push(serialNumber)
    }
    return acc
  }, [])

  return serialNumbers || []
}

export const getSerialNumbersByOtcProduct = (tasks: Task[], otcProducts: OTCProduct[]): SerialNumbersByOtcProduct[] => {
  const serialNumbersByOtcProduct: SerialNumbersByOtcProduct[] = []
  for (const otcProduct of otcProducts) {
    const serializedItemTasks = tasks.filter(
      task => task.productType === 'OTC' && task.key.includes(otcProduct._id) && task.additionalData?.isSerialized,
    )

    if (serializedItemTasks.length > 0) {
      const serialNumbers = serializedItemTasks.reduce((acc: string[], task) => {
        const serialNumber = task.additionalData?.scannedSerialNumber || task.additionalData?.manualSerialNumber
        if (serialNumber) {
          acc.push(serialNumber)
        }
        return acc
      }, [])

      serialNumbersByOtcProduct.push({
        _id: otcProduct._id,
        serialNumbers,
      })
    }
  }

  return serialNumbersByOtcProduct
}

export const getOverriddenItems = (
  tasks: Task[],
  otcProducts?: OTCProduct[],
  packingMaterials?: PackingMaterialItem[],
): OverrideItem[] => {
  const overriddenItems: OverrideItem[] = []
  if (otcProducts) {
    for (const otcProduct of otcProducts) {
      const overriddenTask = tasks.find(
        task => task.productType === 'OTC' && task.key.includes(otcProduct._id) && task.overrideScan,
      )

      if (overriddenTask) {
        overriddenItems.push({
          _id: otcProduct._id,
          productType: 'OTC',
        })
      }
    }
  }

  if (packingMaterials) {
    for (const packingMaterial of packingMaterials) {
      const overriddenTask = tasks.find(
        task => task.productType === 'PM' && task.key.includes(packingMaterial._id) && task.overrideScan,
      )

      if (overriddenTask) {
        overriddenItems.push({
          _id: packingMaterial._id,
          productType: 'PM',
        })
      }
    }
  }
  return overriddenItems
}

export const shouldCompleteTask = (
  requireSerialNumber: boolean,
  additionalData: PackingAdditonalData | undefined,
): boolean => {
  if (!additionalData?.upcScanned) return false

  if (!requireSerialNumber) return true

  if (!additionalData?.isSerialized) return true

  return Boolean(additionalData?.scannedSerialNumber || additionalData?.manualSerialNumber)
}

export const allTasksComplete = (tasks: Task[]): boolean => {
  if (tasks.length === 0) return false
  if (!tasks.every(task => task.completed)) return false

  return tasks.every(task => {
    if (!task.additionalData?.isSerialized) return true
    return Boolean(task.additionalData?.scannedSerialNumber || task.additionalData?.manualSerialNumber)
  })
}
